import { css } from "@emotion/react";
import { useMemo } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { Flex, Text } from "@phoenix/components";
import type { TraceHeaderTraceAnnotationsFragment$key } from "@phoenix/pages/trace/__generated__/TraceHeaderTraceAnnotationsFragment.graphql";
import {
  Summary,
  SummaryValue,
} from "@phoenix/pages/project/AnnotationSummary";

import type { TraceHeaderTraceAnnotationsQuery } from "./__generated__/TraceHeaderTraceAnnotationsQuery.graphql";

const summaryGroupCSS = css`
  align-items: flex-start;
  min-width: 0;
  flex: 1 1 auto;
  max-width: 100%;
  overflow: hidden;
`;

const horizontalScrollableContainerCSS = css`
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 6px;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #64748b #e2e8f0;

  &::-webkit-scrollbar {
    height: 8px !important;
    display: block !important;
  }
  &::-webkit-scrollbar-track {
    background: #e2e8f0 !important;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #64748b !important;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #334155 !important;
  }
`;

/**
 * Renders the trace's own (run-level) annotation summaries.
 *
 * These are the canonical, user-facing evaluation verdicts for a run:
 * one value per metric per trace. Keep step/observation-level annotations in
 * the span views - this group is deliberately trace-scoped.
 */
export function TraceHeaderTraceAnnotations({ traceId }: { traceId: string }) {
  const query = useLazyLoadQuery<TraceHeaderTraceAnnotationsQuery>(
    graphql`
      query TraceHeaderTraceAnnotationsQuery($traceId: ID!) {
        trace: node(id: $traceId) {
          ... on Trace {
            ...TraceHeaderTraceAnnotationsFragment
          }
        }
      }
    `,
    { traceId },
    {
      fetchPolicy: "store-and-network",
    }
  );
  const trace = useFragment<TraceHeaderTraceAnnotationsFragment$key>(
    graphql`
      fragment TraceHeaderTraceAnnotationsFragment on Trace {
        traceAnnotationSummaries {
          name
          meanScore
          labelFractions {
            fraction
            label
          }
        }
      }
    `,
    query.trace
  );
  const summaries = useMemo(
    () =>
      [...trace.traceAnnotationSummaries]
        // Notes are not displayed in summary groups
        .filter((summary) => summary.name !== "note")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [trace.traceAnnotationSummaries]
  );
  if (summaries.length === 0) {
    return null;
  }
  return (
    <Flex
      direction="column"
      gap="size-100"
      css={summaryGroupCSS}
    >
      <Text elementType="h3" size="S" color="text-700">
        Run quality ({summaries.length})
      </Text>
      <div css={horizontalScrollableContainerCSS}>
        <Flex direction="row" gap="size-400">
          {summaries.map((summary) => (
            <Summary name={summary.name} key={summary.name}>
              <SummaryValue
                name={summary.name}
                meanScore={summary.meanScore}
                labelFractions={summary.labelFractions}
              />
            </Summary>
          ))}
        </Flex>
      </div>
    </Flex>
  );
}
